import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import homeAPI from 'services/homeAPI';

import { EventManagerWithPosition } from 'interfaces/eventManager.interface';

import { SortArrowDownIcon } from '@common_components/icons/sortArrowDown.icon';
import { EventManagerIcon } from '@common_components/icons/eventManager.icon';
import { PlusIcon } from '@common_components/icons/plus.icon';
import { GripIcon } from '@common_components/icons/grip.icon';
import Button from '@common_components/buttons/button.component';
import { ArrowShortIcon } from '@common_components/icons/arrowShort.icon';
import { CrossIcon } from '@common_components/icons/cross.icon';
import EmptyState from 'components/common/EmptyState.component';
import { CheckIcon, CommonGlobalService, RootState } from '@umanforge/common';
import { useNavigate } from 'react-router-dom';
import { EyeIcon } from '@common_components/icons/eye.icon';
import ActivationVisualisationModal from 'components/modals/activation_visualisation_modal.component';
import { useSelector } from 'react-redux';

interface ArrayButtonsHomeProps {
  sectorId: string;
  tables: EventManagerWithPosition[];
  setTables: ( tables: EventManagerWithPosition[] ) => void;
  goToTableManagement: ( tableName: string, tableId: string ) => void;
  setPopupVisible: ( visible: boolean ) => void;
  onDragModeChange: ( isDragMode: boolean ) => void;
}

function ArrayButtonsHome( { tables, setTables, goToTableManagement, setPopupVisible, sectorId, onDragModeChange }: ArrayButtonsHomeProps ) {
  const { t } = useTranslation();
  const me = useSelector( ( state: RootState ) => { return state.user.value; } );

  const [ displayedTables, setDisplayedTables ] = useState<EventManagerWithPosition[]>( [] );
  const [ originalTables, setOriginalTables ] = useState<EventManagerWithPosition[]>( [] );
  const [ draggedIndex, setDraggedIndex ] = useState<number | null>( null );
  const [ hoverIndex, setHoverIndex ] = useState<number | null>( null );
  const [ isDragEnabled, setIsDragEnabled ] = useState( false );
  const [ openConfirmVisualisationServiceModal, setOpenConfirmVisualisationServiceModal ] = useState( false );

  const { updateTablePositionsAPI } = homeAPI();

  const navigate = useNavigate();

  const isCreateEventManagerAllowed = ( me != undefined && CommonGlobalService.isAdminOfSector( me, sectorId ) );


  useEffect( () => {
    if ( !isDragEnabled ) {
      const sortedTables = [ ...tables ].sort( ( a, b ) => {return a.position - b.position;} );
      setDisplayedTables( sortedTables );
      setOriginalTables( sortedTables );
    }
  }, [ tables, isDragEnabled ] );

  useEffect( () => {
    setIsDragEnabled( false );
    onDragModeChange( false );
  }, [ sectorId, onDragModeChange ] );

  const onModalClose = () => {
    setOpenConfirmVisualisationServiceModal( false );
  };

  const toggleDrag = () => {
    if ( isDragEnabled ) {
      saveTablePositions();
    }
    const newDragState = !isDragEnabled;
    setIsDragEnabled( newDragState );
    onDragModeChange( newDragState );
  };

  const cancelDrag = () => {
    setDisplayedTables( [ ...originalTables ] );
    setIsDragEnabled( false );
    onDragModeChange( false );
  };

  const handleDragStart = ( index: number ) => {
    setDraggedIndex( index );
  };

  const handleDragOver = ( event: React.DragEvent<HTMLDivElement>, index: number ) => {
    event.preventDefault();
    setHoverIndex( index );
  };

  const handleDrop = () => {
    if ( draggedIndex === null || hoverIndex === null || draggedIndex === hoverIndex ) {
      return;
    }

    const updatedTables = [ ...displayedTables ];
    const [ draggedTable ] = updatedTables.splice( draggedIndex, 1 );
    updatedTables.splice( hoverIndex, 0, draggedTable );

    updatedTables.forEach( ( table, index ) => {
      table.position = index;
    } );

    setDisplayedTables( updatedTables );
    setTables( updatedTables );
    setDraggedIndex( null );
    setHoverIndex( null );
  };

  const handleDragEnd = () => {
    setDraggedIndex( null );
    setHoverIndex( null );
  };

  const saveTablePositions = async () => {
    if ( displayedTables.length === 0 ) {
      return;
    }

    const eventManagers = displayedTables.map( ( table, index ) => {return {
      eventManagerId: table.id,
      position: index,
    };} );

    if ( !sectorId ) {
      console.error( 'Sector ID is missing' );
      return;
    }

    await updateTablePositionsAPI( sectorId, eventManagers );
    setDisplayedTables( [ ...displayedTables ].sort( ( a, b ) => {return a.position - b.position;} ) );
  };

  return (
    <div className="mt-10 rounded-md space-y-6 flex flex-col">
      <div className="w-full mb-6 text-gray-600">
        <div className='flex items-center gap-4 pl-3'>
          <EventManagerIcon className='w-10 h-10'/>
          <h1 className='font-comfortaa text-2xl'>{t( 'translation:event_manager_list' )}</h1>
        </div>
        <div className="w-full flex flex-wrap items-center gap-4 mt-3 pl-3">
          {!isDragEnabled && (
            <>
              {isCreateEventManagerAllowed && (
                <Button
                  label={t( 'translation:create_array' )}
                  icon={<PlusIcon className='group-hover:rotate-[360deg] transition-transform duration-500 w-5 h-5' />}
                  padding='py-3 px-5'
                  textColor='text-green-700'
                  textColorHover='hover:text-green-700'
                  hoverBorderColor='hover:border-transparent'
                  color='bg-green-200'
                  shadow='shadow-none'
                  colorHover='hover:bg-green-300'
                  onClick={() => {return setPopupVisible( true );}}
                />
              )}
            </>
          )}

          {tables.length > 1 && isCreateEventManagerAllowed && (
            isDragEnabled ? (
              <>
                <Button
                  label={t( 'common:cancel' )}
                  icon={<CrossIcon className='w-5 h-5' />}
                  padding='py-3 px-5'
                  textColor='text-gray-600'
                  colorHover='hover:bg-gray-200'
                  hoverBorderColor='hover:border-transparent'
                  color='bg-gray-100'
                  textColorHover='hover:text-gray-600'
                  onClick={cancelDrag}
                />
                <Button
                  label={t( 'translation:confirm_table_order' )}
                  icon={<CheckIcon className='w-5 h-5' />}
                  padding='py-3 px-5'
                  textColor='text-green-700'
                  textColorHover='hover:text-green-700'
                  hoverBorderColor='hover:border-transparent'
                  color='bg-green-200'
                  shadow='shadow-none'
                  colorHover='hover:bg-green-300'
                  onClick={toggleDrag}
                />
              </>
            ) : (
              <Button
                label={t( 'translation:change_table_order' )}
                icon={<SortArrowDownIcon className='w-5 h-5' />}
                padding='py-3 px-5'
                textColor='text-orange-600'
                colorHover='hover:bg-orange-300'
                hoverBorderColor='hover:border-transparent'
                color='bg-orange-200'
                textColorHover='hover:text-orange-600'
                onClick={toggleDrag}
              />
            )
          )}
          {
            !isDragEnabled && tables.length > 0 &&
            <div className='ml-auto max-xl:ml-0'>
              <Button
                label={t( 'translation:global_visualisation' )}
                icon={<EyeIcon className='group-hover:scale-[1.1] transition-transform duration-500 w-5 h-5' />}
                padding='py-3 px-5'
                textColor='text-blue-700'
                textColorHover='hover:text-blue-700'
                hoverBorderColor='hover:border-transparent'
                color='bg-blue-200'
                shadow='shadow-none'
                colorHover='hover:bg-blue-300'
                onClick={ () => {
                  setOpenConfirmVisualisationServiceModal( true );
                } }
              />
            </div>
          }
        </div>
      </div>

      {tables.length === 0 && (
        <EmptyState type="event_managers" />
      )}

      <div className="flex flex-wrap gap-4" onDragOver={event => {return event.preventDefault();}}>

        {displayedTables.map( ( table, index ) => {return (
          <div
            key={index}
            onDragStart={() => {return handleDragStart( index );}}
            onDragOver={event => {return handleDragOver( event, index );}}
            onDrop={handleDrop}
            onDragEnd={handleDragEnd}
            className={`${isDragEnabled ? ( hoverIndex === index ? 'bg-green-100 border-green-500'
              : ( draggedIndex === index ? 'bg-orange-100 border-orange-500' : 'border-orange-200' ) )
              : 'border-transparent'} 
              ${isDragEnabled ? 'border-dashed rounded-xl' : ''} trans-fast border-2 w-[calc(50%-8px)] p-2`}>
            <div
              className={`w-full bg-orange-100 shadow-md rounded-full relative ${isDragEnabled ? 'cursor-move' : 'cursor-pointer'} flex justify-center trans-fast group text-orange-600 flex-center h-20`}
              onClick={() => {
                return (
                  isDragEnabled ? null : goToTableManagement( table.name, table.id )
                );}}
              draggable={isDragEnabled}
            >
              <div className="absolute inset-0 z-0 rounded-full opacity-0 group-hover:opacity-100 group-hover:bg-orange-200 trans-fast scale-75 group-hover:scale-100"/>
              <div className='z-10 ml-2 flex-center bg-orange-base-light border-2 shadow-md border-orange-base-light rounded-full p-3 text-white'>
                <EventManagerIcon className='w-10 h-10'/>
              </div>
              <p className='z-10 relative w-full text-center text-2xl'>{table.name}</p>
              {!isDragEnabled ? (
                <div className='z-10 mr-3 group-hover:opacity-100 rotate-0 opacity-0 trans-fast bg-white text-orange-base group-hover:-rotate-45 shadow-md rounded-full p-3'>
                  <ArrowShortIcon className='w-7 h-7'/>
                </div>
              ) : (
                <div className='z-10 mr-3 trans-fast bg-white text-orange-base shadow-md rounded-full p-3'>
                  <GripIcon className='w-7 h-7'/>
                </div>
              )}
            </div>
          </div>
        );} )}
      </div>

      <ActivationVisualisationModal
        isOpen={ openConfirmVisualisationServiceModal }
        onClose={ onModalClose }
        sectorId={ sectorId }
        callback={ () => { navigate( '/Visualisation/EventManagers' ); } }
      />

    </div>
  );
}

export default ArrayButtonsHome;
